import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { db } from "../firebaseConfig";
// Legacy stylesheet stays imported (it still carries global rules); new
// styling is scoped under .opsui- (see OpsUi.css).
import "./Vehicles.css";
import {
  OpsModal,
  ConfirmDialog,
  Pill,
  DuePill,
  Field,
  EmptyState,
  StateSelect,
  SortHeader,
  ReminderRecipientsCard,
  dueStatus,
  parseDateOnly,
  addDays,
  toDateKey,
  formatDate,
} from "./OpsUi";

// Field names below are the ones the vehicle reminder Cloud Function reads:
//   inspectionExp, registrationExp  -> expiration reminders
//   vehicleServiceDate (+ vehicleServiceNote) -> service follow-up reminders
//   nextServiceDue, nextServiceMileage + mileage -> service-due reminders
//   canceled / status === "Canceled" -> vehicle skipped
//   notificationLog.<field>.<days> -> de-dupe log written by the function
const EMPTY_VEHICLE = {
  licensePlate: "",
  vin: "",
  make: "",
  model: "",
  year: "",
  mileage: "",
  state: "",
  inspection: "",
  inspectionExp: "",
  emissions: "",
  registration: "",
  registrationExp: "",
  vehicleServiceDate: "",
  vehicleServiceNote: "",
  nextServiceDue: "",
  nextServiceMileage: "",
  fuel: "",
  loudounLandfill: "",
  notes: "",
  fileUpload: [],
};

// Days after the last service the follow-up reminder fires (matches the
// backend's vehicleServiceDate reminders at +90 / +105 days).
const SERVICE_INTERVAL_DAYS = 90;
const MILEAGE_SOON = 500;
const REMINDER_FIELDS = ["inspectionExp", "registrationExp", "vehicleServiceDate", "nextServiceDue"];

const toNumber = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null;
};

const isCanceled = (v) =>
  v.canceled === true || ["canceled", "cancelled"].includes(String(v.status || "").toLowerCase());

const vehicleTitle = (v) =>
  [v.year, v.make, v.model].filter(Boolean).join(" ") || v.licensePlate || "Vehicle";

// When is service due? Explicit nextServiceDue wins; otherwise last service + 90 days.
const serviceDueDate = (v) => {
  if (v.nextServiceDue) return { value: v.nextServiceDue, derived: false };
  const last = parseDateOnly(v.vehicleServiceDate);
  if (!last) return null;
  return { value: toDateKey(addDays(last, SERVICE_INTERVAL_DAYS)), derived: true };
};

const mileageStatus = (v) => {
  const target = toNumber(v.nextServiceMileage);
  const current = toNumber(v.mileage);
  if (target === null || current === null) return null;
  const remaining = target - current;
  if (remaining <= 0) return { tone: "danger", label: `Over by ${Math.abs(remaining).toLocaleString()} mi`, rank: 3, remaining };
  if (remaining <= MILEAGE_SOON) return { tone: "warn", label: `${remaining.toLocaleString()} mi left`, rank: 2, remaining };
  return { tone: "success", label: `${remaining.toLocaleString()} mi left`, rank: 1, remaining };
};

// All the things that can be due on a vehicle, worst first.
const vehicleAlerts = (v) => {
  const items = [];
  const push = (what, s, value) => {
    if (s && s.tone !== "neutral") items.push({ what, value, ...s });
  };
  push("Inspection", dueStatus(v.inspectionExp), v.inspectionExp);
  push("Registration", dueStatus(v.registrationExp), v.registrationExp);
  const svc = serviceDueDate(v);
  if (svc) push("Service", dueStatus(svc.value, { soonLabel: "Due soon" }), svc.value);
  const miles = mileageStatus(v);
  if (miles) items.push({ what: "Service (miles)", ...miles });
  return items.sort((a, b) => b.rank - a.rank);
};

const yearOptions = () => {
  const max = new Date().getFullYear() + 1;
  return Array.from({ length: max - 1990 + 1 }, (_, i) => max - i);
};

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formMode, setFormMode] = useState(null); // "add" | "edit"
  const [formData, setFormData] = useState({ ...EMPTY_VEHICLE });
  const [formFiles, setFormFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showAddNotePopup, setShowAddNotePopup] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteFiles, setNoteFiles] = useState([]);
  const [showServicePopup, setShowServicePopup] = useState(false);
  const [serviceForm, setServiceForm] = useState({ date: "", note: "", mileage: "", nextServiceDue: "", nextServiceMileage: "" });
  const [confirmDeleteVehicle, setConfirmDeleteVehicle] = useState(null);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [view, setView] = useState("all"); // all | attention | active | canceled
  const [sort, setSort] = useState({ key: "urgency", dir: "desc" });

  const storage = getStorage();

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const snap = await getDocs(collection(db, "vehicles"));
        setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error loading vehicles:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const replaceVehicle = (next) => {
    setVehicles((prev) => prev.map((v) => (v.id === next.id ? next : v)));
    setSelectedVehicle((prev) => (prev && prev.id === next.id ? next : prev));
  };

  const uploadVehicleFiles = async (vehicleId, files) => {
    const urls = [];
    for (const file of Array.from(files || [])) {
      const safe = `${Date.now()}_${file.name}`.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileRef = ref(storage, `vehicles/${vehicleId}/${safe}`);
      await uploadBytes(fileRef, file, { contentType: file.type || undefined });
      urls.push({ fileName: file.name, fileURL: await getDownloadURL(fileRef) });
    }
    return urls;
  };

  // Clear the reminder de-dupe log for any reminder date that changed, so the
  // new date can trigger its own emails.
  const resetLogFor = (before = {}, after = {}) => {
    const log = { ...(before.notificationLog || {}) };
    let changed = false;
    REMINDER_FIELDS.forEach((f) => {
      if ((before[f] || "") !== (after[f] || "") && log[f]) {
        delete log[f];
        changed = true;
      }
    });
    return changed ? { notificationLog: log } : {};
  };

  // ------------------------------------------------------------ add / edit
  const onFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_VEHICLE });
    setFormFiles([]);
    setFormMode("add");
  };

  const openEdit = () => {
    if (!selectedVehicle) return;
    setFormData({ ...EMPTY_VEHICLE, ...selectedVehicle });
    setFormFiles([]);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setFormFiles([]);
  };

  const handleAddVehicle = async () => {
    if (!String(formData.licensePlate || "").trim() && !String(formData.vin || "").trim()) {
      alert("Please enter a License Plate or VIN.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, status: "Active", canceled: false };
      delete payload.id;
      const docRef = await addDoc(collection(db, "vehicles"), payload);
      if (formFiles.length) {
        const uploaded = await uploadVehicleFiles(docRef.id, formFiles);
        payload.fileUpload = [...(payload.fileUpload || []), ...uploaded.map((f) => f.fileURL)];
        await updateDoc(doc(db, "vehicles", docRef.id), { fileUpload: payload.fileUpload });
      }
      setVehicles((prev) => [...prev, { id: docRef.id, ...payload }]);
      closeForm();
    } catch (error) {
      console.error("Error adding vehicle:", error);
      alert("Could not add vehicle.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedVehicle) return;
    setSaving(true);
    try {
      const uploaded = formFiles.length ? await uploadVehicleFiles(selectedVehicle.id, formFiles) : [];
      const payload = {
        ...formData,
        ...resetLogFor(selectedVehicle, formData),
        fileUpload: [
          ...(Array.isArray(formData.fileUpload) ? formData.fileUpload : []),
          ...uploaded.map((f) => f.fileURL),
        ],
      };
      const updatePayload = { ...payload };
      delete updatePayload.id;
      await updateDoc(doc(db, "vehicles", selectedVehicle.id), updatePayload);
      replaceVehicle({ ...payload, id: selectedVehicle.id });
      closeForm();
    } catch (error) {
      console.error("Error updating vehicle:", error);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------ actions
  const handleToggleCanceled = async (vehicle) => {
    const nextCanceled = !isCanceled(vehicle);
    try {
      await updateDoc(doc(db, "vehicles", vehicle.id), {
        canceled: nextCanceled,
        status: nextCanceled ? "Canceled" : "Active",
      });
      replaceVehicle({ ...vehicle, canceled: nextCanceled, status: nextCanceled ? "Canceled" : "Active" });
    } catch (e) {
      console.error("Error updating vehicle status:", e);
      alert("Could not update status.");
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteVehicle) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, "vehicles", confirmDeleteVehicle.id));
      setVehicles((prev) => prev.filter((v) => v.id !== confirmDeleteVehicle.id));
      if (selectedVehicle?.id === confirmDeleteVehicle.id) setSelectedVehicle(null);
      setConfirmDeleteVehicle(null);
    } catch (e) {
      console.error("Error deleting vehicle:", e);
      alert("Could not delete vehicle.");
    } finally {
      setBusy(false);
    }
  };

  const closeAddNote = () => {
    setShowAddNotePopup(false);
    setNewNote("");
    setNoteFiles([]);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() && noteFiles.length === 0) {
      alert("Please enter a note or upload a file before saving.");
      return;
    }
    setBusy(true);
    try {
      const uploaded = await uploadVehicleFiles(selectedVehicle.id, noteFiles);
      const entry = {
        text: newNote.trim(),
        timestamp: new Date().toLocaleString(),
        files: uploaded,
      };
      const noteLog = [...(Array.isArray(selectedVehicle.noteLog) ? selectedVehicle.noteLog : []), entry];
      const fileUpload = [
        ...(Array.isArray(selectedVehicle.fileUpload) ? selectedVehicle.fileUpload : []),
        ...uploaded.map((f) => f.fileURL),
      ];
      await updateDoc(doc(db, "vehicles", selectedVehicle.id), { noteLog, fileUpload });
      replaceVehicle({ ...selectedVehicle, noteLog, fileUpload });
      closeAddNote();
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Could not save note.");
    } finally {
      setBusy(false);
    }
  };

  const openLogService = () => {
    setServiceForm({
      date: toDateKey(new Date()),
      note: "",
      mileage: selectedVehicle?.mileage || "",
      nextServiceDue: "",
      nextServiceMileage: "",
    });
    setShowServicePopup(true);
  };

  const handleLogService = async () => {
    if (!serviceForm.date) {
      alert("Please choose the service date.");
      return;
    }
    setBusy(true);
    try {
      const v = selectedVehicle;
      const history = [
        ...(Array.isArray(v.serviceHistory) ? v.serviceHistory : []),
        {
          date: serviceForm.date,
          note: serviceForm.note.trim(),
          mileage: serviceForm.mileage,
          loggedAt: new Date().toISOString(),
        },
      ];
      const next = {
        ...v,
        vehicleServiceDate: serviceForm.date,
        vehicleServiceNote: serviceForm.note.trim(),
        mileage: serviceForm.mileage || v.mileage || "",
        nextServiceDue: serviceForm.nextServiceDue,
        nextServiceMileage: serviceForm.nextServiceMileage,
        serviceHistory: history,
      };
      const patch = {
        vehicleServiceDate: next.vehicleServiceDate,
        vehicleServiceNote: next.vehicleServiceNote,
        mileage: next.mileage,
        nextServiceDue: next.nextServiceDue,
        nextServiceMileage: next.nextServiceMileage,
        serviceHistory: history,
        ...resetLogFor(v, next),
      };
      await updateDoc(doc(db, "vehicles", v.id), patch);
      replaceVehicle({ ...next, ...patch });
      setShowServicePopup(false);
    } catch (e) {
      console.error("Error logging service:", e);
      alert("Could not log service.");
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------ list
  const handleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const enriched = useMemo(
    () => vehicles.map((v) => ({ v, alerts: vehicleAlerts(v), canceled: isCanceled(v) })),
    [vehicles]
  );

  const counts = useMemo(() => {
    const active = enriched.filter((e) => !e.canceled);
    return {
      all: enriched.length,
      active: active.length,
      canceled: enriched.length - active.length,
      attention: active.filter((e) => e.alerts[0] && e.alerts[0].rank >= 2).length,
      overdue: active.filter((e) => e.alerts[0] && e.alerts[0].rank >= 3).length,
    };
  }, [enriched]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const urgency = (e) => (e.canceled ? -1 : e.alerts[0]?.rank || 0);
    return enriched
      .filter((e) => {
        if (view === "active" && e.canceled) return false;
        if (view === "canceled" && !e.canceled) return false;
        if (view === "attention" && (e.canceled || !(e.alerts[0] && e.alerts[0].rank >= 2))) return false;
        if (!q) return true;
        const v = e.v;
        return [v.licensePlate, v.vin, v.make, v.model, v.year, v.state]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const dir = sort.dir === "asc" ? 1 : -1;
        if (sort.key === "urgency") {
          const d = urgency(a) - urgency(b);
          if (d !== 0) return d * dir;
        } else {
          const cmp = String(a.v[sort.key] || "").localeCompare(String(b.v[sort.key] || ""), undefined, {
            numeric: true,
            sensitivity: "base",
          });
          if (cmp !== 0) return cmp * dir;
        }
        return String(a.v.licensePlate || "").localeCompare(String(b.v.licensePlate || ""));
      });
  }, [enriched, search, view, sort]);

  // ------------------------------------------------------------ render: form
  const renderForm = () => (
    <div className="opsui-form-grid">
      <div className="opsui-section-title">Vehicle</div>
      <Field label="License Plate">
        <input className="opsui-input" name="licensePlate" value={formData.licensePlate || ""} onChange={onFormChange} />
      </Field>
      <Field label="State">
        <StateSelect value={formData.state} onChange={onFormChange} />
      </Field>
      <Field label="VIN" wide>
        <input className="opsui-input" name="vin" value={formData.vin || ""} onChange={onFormChange} />
      </Field>
      <Field label="Year">
        <select className="opsui-select" name="year" value={formData.year || ""} onChange={onFormChange}>
          <option value="">Select year</option>
          {yearOptions().map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
          {formData.year && !yearOptions().includes(Number(formData.year)) && (
            <option value={formData.year}>{formData.year}</option>
          )}
        </select>
      </Field>
      <Field label="Make">
        <input className="opsui-input" name="make" value={formData.make || ""} onChange={onFormChange} />
      </Field>
      <Field label="Model">
        <input className="opsui-input" name="model" value={formData.model || ""} onChange={onFormChange} />
      </Field>
      <Field label="Current Mileage">
        <input className="opsui-input" name="mileage" inputMode="numeric" value={formData.mileage || ""} onChange={onFormChange} />
      </Field>

      <div className="opsui-section-title">Inspection & registration</div>
      <Field label="Last Inspection">
        <input className="opsui-input" type="date" name="inspection" value={formData.inspection || ""} onChange={onFormChange} />
      </Field>
      <Field label="Inspection Expires" hint="Email reminders are sent from this date">
        <input
          className="opsui-input"
          type="date"
          name="inspectionExp"
          value={(formData.inspectionExp || "").slice(0, 10)}
          onChange={onFormChange}
        />
      </Field>
      <Field label="Last Registration">
        <input className="opsui-input" type="date" name="registration" value={formData.registration || ""} onChange={onFormChange} />
      </Field>
      <Field label="Registration Expires" hint="Email reminders are sent from this date">
        <input
          className="opsui-input"
          type="date"
          name="registrationExp"
          value={(formData.registrationExp || "").slice(0, 10)}
          onChange={onFormChange}
        />
      </Field>
      <Field label="Emissions">
        <input className="opsui-input" type="date" name="emissions" value={formData.emissions || ""} onChange={onFormChange} />
      </Field>
      <div />

      <div className="opsui-section-title">Service</div>
      <Field label="Last Service Date" hint={`Follow-up reminder fires ${SERVICE_INTERVAL_DAYS} days after this date`}>
        <input
          className="opsui-input"
          type="date"
          name="vehicleServiceDate"
          value={formData.vehicleServiceDate || ""}
          onChange={onFormChange}
        />
      </Field>
      <Field label="Next Service Due" hint="Leave blank to use last service + 90 days">
        <input className="opsui-input" type="date" name="nextServiceDue" value={formData.nextServiceDue || ""} onChange={onFormChange} />
      </Field>
      <Field label="Next Service At (miles)" hint="Optional — flags the vehicle when mileage gets close">
        <input
          className="opsui-input"
          name="nextServiceMileage"
          inputMode="numeric"
          value={formData.nextServiceMileage || ""}
          onChange={onFormChange}
        />
      </Field>
      <Field label="Last Service Note">
        <input className="opsui-input" name="vehicleServiceNote" value={formData.vehicleServiceNote || ""} onChange={onFormChange} />
      </Field>

      <div className="opsui-section-title">Other</div>
      <Field label="Fuel">
        <input className="opsui-input" name="fuel" value={formData.fuel || ""} onChange={onFormChange} />
      </Field>
      <Field label="Loudoun Landfill">
        <input className="opsui-input" name="loudounLandfill" value={formData.loudounLandfill || ""} onChange={onFormChange} />
      </Field>
      <Field label="Notes" wide>
        <textarea className="opsui-textarea" name="notes" value={formData.notes || ""} onChange={onFormChange} />
      </Field>
      <Field
        label="Files"
        wide
        hint={formFiles.length ? `${formFiles.length} file(s) will upload when you save.` : "Registration card, inspection report, receipts…"}
      >
        <input className="opsui-file" type="file" multiple onChange={(e) => setFormFiles(Array.from(e.target.files || []))} />
      </Field>
    </div>
  );

  // ------------------------------------------------------------ render: details
  const renderDetails = (v) => {
    const svc = serviceDueDate(v);
    const miles = mileageStatus(v);
    const noteLog = Array.isArray(v.noteLog) ? v.noteLog : [];
    const files = Array.isArray(v.fileUpload) ? v.fileUpload : [];
    const history = Array.isArray(v.serviceHistory) ? [...v.serviceHistory].reverse() : [];
    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div className="opsui-pills">
          {isCanceled(v) ? <Pill tone="danger">Canceled</Pill> : <Pill tone="success">Active</Pill>}
          {v.licensePlate && (
            <Pill tone="neutral" plain>
              {v.licensePlate}
              {v.state ? ` · ${v.state}` : ""}
            </Pill>
          )}
        </div>

        <div className="opsui-card" style={{ margin: 0 }}>
          <div className="opsui-card-head">
            <h3 className="opsui-card-title">What’s due</h3>
          </div>
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <tbody>
                <tr>
                  <td>Inspection</td>
                  <td>
                    <DuePill value={v.inspectionExp} />
                  </td>
                  <td className="opsui-cell-sub">Last: {formatDate(v.inspection) || "—"}</td>
                </tr>
                <tr>
                  <td>Registration</td>
                  <td>
                    <DuePill value={v.registrationExp} />
                  </td>
                  <td className="opsui-cell-sub">Last: {formatDate(v.registration) || "—"}</td>
                </tr>
                <tr>
                  <td>Service</td>
                  <td>
                    {svc ? <DuePill value={svc.value} /> : <span className="opsui-muted">—</span>}
                    {svc?.derived && <div className="opsui-cell-sub">Last service + {SERVICE_INTERVAL_DAYS} days</div>}
                  </td>
                  <td className="opsui-cell-sub">
                    Last: {formatDate(v.vehicleServiceDate) || "—"}
                    {v.vehicleServiceNote ? ` · ${v.vehicleServiceNote}` : ""}
                  </td>
                </tr>
                <tr>
                  <td>Mileage</td>
                  <td>
                    {miles ? <Pill tone={miles.tone}>{miles.label}</Pill> : <span className="opsui-muted">—</span>}
                  </td>
                  <td className="opsui-cell-sub">
                    Now: {v.mileage ? Number(toNumber(v.mileage)).toLocaleString() : "—"}
                    {v.nextServiceMileage ? ` · service at ${Number(toNumber(v.nextServiceMileage)).toLocaleString()}` : ""}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <dl className="opsui-dl">
          <div>
            <dt>VIN</dt>
            <dd>{v.vin || "—"}</dd>
          </div>
          <div>
            <dt>Emissions</dt>
            <dd>{formatDate(v.emissions) || "—"}</dd>
          </div>
          <div>
            <dt>Fuel</dt>
            <dd>{v.fuel || "—"}</dd>
          </div>
          <div>
            <dt>Loudoun Landfill</dt>
            <dd>{v.loudounLandfill || "—"}</dd>
          </div>
          <div className="is-wide">
            <dt>Notes</dt>
            <dd>{v.notes || "—"}</dd>
          </div>
        </dl>

        {history.length > 0 && (
          <div>
            <div className="opsui-label" style={{ marginBottom: 8 }}>Service history</div>
            <ul className="opsui-list">
              {history.map((h, i) => (
                <li key={i} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <div className="opsui-list-meta">
                      {formatDate(h.date)}
                      {h.mileage ? ` · ${Number(toNumber(h.mileage)).toLocaleString()} mi` : ""}
                    </div>
                    <div style={{ fontSize: 14 }}>{h.note || "Service performed"}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>Notes log</div>
          {noteLog.length ? (
            <ul className="opsui-list">
              {noteLog.map((n, i) => (
                <li key={i} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <div className="opsui-list-meta">{n.timestamp}</div>
                    <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{n.text}</div>
                    {Array.isArray(n.files) && n.files.length > 0 && (
                      <div className="opsui-pills" style={{ marginTop: 6 }}>
                        {n.files.map((f, fi) => (
                          <a key={fi} className="opsui-link" href={f.fileURL} target="_blank" rel="noopener noreferrer">
                            📎 {f.fileName}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 14 }}>No notes yet.</span>
          )}
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>Files</div>
          {files.length ? (
            <div className="opsui-pills">
              {files.map((f, i) =>
                String(f).startsWith("blob:") ? null : (
                  <a key={i} className="opsui-link" href={f} target="_blank" rel="noopener noreferrer">
                    📄 File {i + 1}
                  </a>
                )
              )}
            </div>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 14 }}>No files.</span>
          )}
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------ render
  return (
    <div className="opsui-root opsui-page">
      <div className="opsui-header">
        <div>
          <h1 className="opsui-title">Fleet Vehicles</h1>
          <p className="opsui-subtitle">Inspections, registrations and service schedules — with automatic email reminders.</p>
        </div>
        <div className="opsui-header-actions">
          <button type="button" className="opsui-btn is-primary" onClick={openAdd}>
            + Add vehicle
          </button>
        </div>
      </div>

      <div className="opsui-stats">
        <div className="opsui-stat">
          <div className="opsui-stat-label">Active vehicles</div>
          <div className="opsui-stat-value">{counts.active}</div>
        </div>
        <div className={`opsui-stat ${counts.attention - counts.overdue ? "is-warn" : ""}`}>
          <div className="opsui-stat-label">Due soon</div>
          <div className="opsui-stat-value">{counts.attention - counts.overdue}</div>
        </div>
        <div className={`opsui-stat ${counts.overdue ? "is-danger" : ""}`}>
          <div className="opsui-stat-label">Overdue</div>
          <div className="opsui-stat-value">{counts.overdue}</div>
        </div>
        <div className="opsui-stat">
          <div className="opsui-stat-label">Canceled</div>
          <div className="opsui-stat-value">{counts.canceled}</div>
        </div>
      </div>

      <div className="opsui-tabs">
        {[
          ["all", "All", counts.all],
          ["attention", "Needs attention", counts.attention],
          ["active", "Active", counts.active],
          ["canceled", "Canceled", counts.canceled],
        ].map(([key, label, count]) => (
          <button key={key} type="button" className={`opsui-tab${view === key ? " is-active" : ""}`} onClick={() => setView(key)}>
            {label}
            <span className="opsui-tab-count">{count}</span>
          </button>
        ))}
      </div>

      <div className="opsui-card">
        <div className="opsui-toolbar">
          <input
            className="opsui-input"
            type="search"
            placeholder="Search plate, VIN, make, model…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="opsui-select"
            value={`${sort.key}:${sort.dir}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(":");
              setSort({ key, dir });
            }}
          >
            <option value="urgency:desc">Sort: most urgent first</option>
            <option value="licensePlate:asc">License Plate (A-Z)</option>
            <option value="licensePlate:desc">License Plate (Z-A)</option>
            <option value="year:desc">Year (newest)</option>
            <option value="make:asc">Make (A-Z)</option>
          </select>
          <span className="opsui-toolbar-spacer" />
          <span className="opsui-count">{rows.length} shown</span>
        </div>

        {loading ? (
          <EmptyState title="Loading vehicles…" />
        ) : rows.length === 0 ? (
          vehicles.length === 0 ? (
            <EmptyState
              title="No vehicles yet"
              action={
                <button type="button" className="opsui-btn is-primary" onClick={openAdd}>
                  + Add your first vehicle
                </button>
              }
            >
              Add each truck so inspections, registrations and service never slip.
            </EmptyState>
          ) : (
            <EmptyState title={view === "attention" ? "Nothing needs attention 🎉" : "No vehicles match"} />
          )
        ) : (
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <thead>
                <tr>
                  <SortHeader label="Vehicle" sortKey="licensePlate" sort={sort} onSort={handleSort} />
                  <th>Inspection</th>
                  <th className="opsui-hide-sm">Registration</th>
                  <th className="opsui-hide-sm">Service</th>
                  <SortHeader label="Status" sortKey="urgency" sort={sort} onSort={handleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ v, alerts, canceled }) => {
                  const svc = serviceDueDate(v);
                  const miles = mileageStatus(v);
                  const top = alerts[0];
                  return (
                    <tr
                      key={v.id}
                      className={`is-clickable${canceled ? " is-muted" : ""}`}
                      onClick={() => setSelectedVehicle(v)}
                    >
                      <td>
                        <div className="opsui-cell-main">{v.licensePlate || "No plate"}</div>
                        <div className="opsui-cell-sub">
                          {[v.year, v.make, v.model].filter(Boolean).join(" ")}
                          {v.vin ? ` · VIN …${String(v.vin).slice(-6)}` : ""}
                        </div>
                      </td>
                      <td>
                        <DuePill value={v.inspectionExp} />
                      </td>
                      <td className="opsui-hide-sm">
                        <DuePill value={v.registrationExp} />
                      </td>
                      <td className="opsui-hide-sm">
                        {svc ? <DuePill value={svc.value} /> : null}
                        {miles && (
                          <div style={{ marginTop: svc ? 4 : 0 }}>
                            <Pill tone={miles.tone} plain>
                              {miles.label}
                            </Pill>
                          </div>
                        )}
                        {!svc && !miles && <span className="opsui-muted">—</span>}
                      </td>
                      <td>
                        {canceled ? (
                          <Pill tone="neutral">Canceled</Pill>
                        ) : top && top.rank >= 3 ? (
                          <Pill tone="danger">Overdue</Pill>
                        ) : top && top.rank === 2 ? (
                          <Pill tone="warn">Due soon</Pill>
                        ) : top ? (
                          <Pill tone="success">OK</Pill>
                        ) : (
                          <Pill tone="neutral">No dates</Pill>
                        )}
                        {top && top.rank >= 2 && !canceled && <div className="opsui-cell-sub">{top.what}</div>}
                      </td>
                      <td className="opsui-cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="opsui-btn is-sm" onClick={() => setSelectedVehicle(v)}>
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReminderRecipientsCard
        moduleKey="vehicles"
        title="Vehicle reminder emails"
        description="Who gets emailed when an inspection, registration or service is coming due."
        includeVehicleOptIns
        schedule={
          "Inspection & registration: 60 and 30 days before expiration, then 1, 15, 30 and 60 days after. " +
          "Service: 14 days before and on the Next Service Due date, then 7 and 30 days after; " +
          `follow-up ${SERVICE_INTERVAL_DAYS} and ${SERVICE_INTERVAL_DAYS + 15} days after the last service; ` +
          `and when mileage is within ${MILEAGE_SOON} miles of “Next Service At”. Canceled vehicles are skipped.`
        }
      />

      {/* ---------- details ---------- */}
      {selectedVehicle && !formMode && (
        <OpsModal
          title={vehicleTitle(selectedVehicle)}
          subtitle="Vehicle details"
          onClose={() => setSelectedVehicle(null)}
          footer={
            <>
              <div className="opsui-foot-left">
                <button type="button" className="opsui-btn is-danger" onClick={() => setConfirmDeleteVehicle(selectedVehicle)}>
                  Delete
                </button>
                <button type="button" className="opsui-btn" onClick={() => handleToggleCanceled(selectedVehicle)}>
                  {isCanceled(selectedVehicle) ? "Reactivate" : "Mark canceled"}
                </button>
              </div>
              <button type="button" className="opsui-btn" onClick={() => setShowAddNotePopup(true)}>
                Add note
              </button>
              <button type="button" className="opsui-btn" onClick={openLogService}>
                Log service
              </button>
              <button type="button" className="opsui-btn" onClick={openEdit}>
                Edit
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={() => setSelectedVehicle(null)}>
                Close
              </button>
            </>
          }
        >
          {renderDetails(selectedVehicle)}
        </OpsModal>
      )}

      {/* ---------- add / edit ---------- */}
      {formMode && (
        <OpsModal
          title={formMode === "add" ? "Add vehicle" : `Edit ${vehicleTitle(formData)}`}
          onClose={closeForm}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="opsui-btn is-primary"
                onClick={formMode === "add" ? handleAddVehicle : handleUpdate}
                disabled={saving}
              >
                {saving ? "Saving…" : formMode === "add" ? "Save vehicle" : "Save changes"}
              </button>
            </>
          }
        >
          {renderForm()}
        </OpsModal>
      )}

      {/* ---------- add note ---------- */}
      {showAddNotePopup && selectedVehicle && (
        <OpsModal
          title="Add note"
          subtitle={vehicleTitle(selectedVehicle)}
          onClose={closeAddNote}
          width={520}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={closeAddNote} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={handleAddNote} disabled={busy}>
                {busy ? "Saving…" : "Save note"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 12 }}>
            <Field label="Note">
              <textarea className="opsui-textarea" value={newNote} onChange={(e) => setNewNote(e.target.value)} />
            </Field>
            <Field label="Attachments">
              <input className="opsui-file" type="file" multiple onChange={(e) => setNoteFiles(Array.from(e.target.files || []))} />
            </Field>
          </div>
        </OpsModal>
      )}

      {/* ---------- log service ---------- */}
      {showServicePopup && selectedVehicle && (
        <OpsModal
          title="Log service"
          subtitle={vehicleTitle(selectedVehicle)}
          onClose={() => setShowServicePopup(false)}
          width={560}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setShowServicePopup(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={handleLogService} disabled={busy}>
                {busy ? "Saving…" : "Save service"}
              </button>
            </>
          }
        >
          <div className="opsui-form-grid">
            <Field label="Service Date" required>
              <input
                className="opsui-input"
                type="date"
                value={serviceForm.date}
                onChange={(e) => setServiceForm((p) => ({ ...p, date: e.target.value }))}
              />
            </Field>
            <Field label="Mileage at Service">
              <input
                className="opsui-input"
                inputMode="numeric"
                value={serviceForm.mileage}
                onChange={(e) => setServiceForm((p) => ({ ...p, mileage: e.target.value }))}
              />
            </Field>
            <Field label="What was done" wide>
              <input
                className="opsui-input"
                placeholder="Oil change, tires rotated…"
                value={serviceForm.note}
                onChange={(e) => setServiceForm((p) => ({ ...p, note: e.target.value }))}
              />
            </Field>
            <Field label="Next Service Due" hint="Blank = service date + 90 days">
              <input
                className="opsui-input"
                type="date"
                value={serviceForm.nextServiceDue}
                onChange={(e) => setServiceForm((p) => ({ ...p, nextServiceDue: e.target.value }))}
              />
            </Field>
            <Field label="Next Service At (miles)">
              <input
                className="opsui-input"
                inputMode="numeric"
                value={serviceForm.nextServiceMileage}
                onChange={(e) => setServiceForm((p) => ({ ...p, nextServiceMileage: e.target.value }))}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {confirmDeleteVehicle && (
        <ConfirmDialog
          title="Delete vehicle?"
          message={
            <>
              <strong>{vehicleTitle(confirmDeleteVehicle)}</strong>
              {confirmDeleteVehicle.licensePlate ? ` (${confirmDeleteVehicle.licensePlate})` : ""} will be permanently
              removed. To keep its history, mark it canceled instead.
            </>
          }
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteVehicle(null)}
          busy={busy}
        />
      )}
    </div>
  );
};

export default Vehicles;
